import { ArrowRight, CalendarDays, CheckCircle2, ListChecks, Mail, ShieldCheck, UsersRound } from 'lucide-react';
import { Link } from 'react-router-dom';
import PublicSiteLayout from '../components/PublicSiteLayout';

export const useCasePages = {
  schoolEmail: {
    eyebrow: 'School email assistant',
    title: 'Find the action hiding inside every school email.',
    lede: 'Little Adda helps parents separate deadlines, forms, fees, supplies, and schedule changes from long newsletters and scattered teacher updates.',
    icon: Mail,
    problemTitle: 'School communication was written to inform—not to organize a family.',
    problemCopy: 'One message can contain a picture-day date, a lunch-order deadline, a volunteer form, and three announcements that require no action. Parents have to interpret the message, remember each commitment, and tell the right caregiver.',
    steps: [
      ['Bring in the update', 'Forward or connect the school communication parents already receive.'],
      ['Find what matters', 'Little Adda identifies likely dates, deadlines, requests, attachments, and changes.'],
      ['Review the suggestions', 'A parent can approve, edit, or dismiss each proposed reminder, task, or calendar item.'],
    ],
    examples: ['Lunch ordering closes tonight', 'Picture day is Friday', 'Volunteer form is attached', 'Bring art supplies on Monday'],
  },
  familyCalendar: {
    eyebrow: 'Family calendar',
    title: 'Turn school dates into a calendar the family can trust.',
    lede: 'Little Adda drafts calendar suggestions from school communication while keeping parents in control of what becomes part of the family schedule.',
    icon: CalendarDays,
    problemTitle: 'The date is easy to read. The coordination around it is the hard part.',
    problemCopy: 'A field trip, early dismissal, performance, or deadline affects transportation, work schedules, supplies, and multiple caregivers. Copying a date into a calendar is only the first step.',
    steps: [
      ['Detect the date', 'Little Adda finds dates and schedule changes described in school updates.'],
      ['Draft useful context', 'The suggestion keeps the source and relevant details close to the calendar action.'],
      ['Approve before sharing', 'A parent reviews the suggestion before it joins the family plan.'],
    ],
    examples: ['Early dismissal at 12:30 PM', 'Class performance next Thursday', 'No school on staff-development day', 'Permission slip due before the field trip'],
  },
  sharedTasks: {
    eyebrow: 'Shared family tasks',
    title: 'Give every school responsibility a clear next owner.',
    lede: 'Little Adda turns requests found in school communication into parent-approved tasks that caregivers can coordinate without another group-chat scramble.',
    icon: ListChecks,
    problemTitle: 'Knowing about a task is different from knowing who will do it.',
    problemCopy: 'School requests often arrive when one caregiver is busy and another never saw the message. Without an explicit next step, forms, payments, supplies, and follow-ups stay mentally assigned to everyone—and operationally assigned to no one.',
    steps: [
      ['Recognize the request', 'Little Adda identifies likely actions such as completing a form or bringing supplies.'],
      ['Draft a concrete task', 'The proposed task captures the action and relevant timing for parent review.'],
      ['Coordinate caregivers', 'Approved work can become part of the shared family flow.'],
    ],
    examples: ['Complete the volunteer form', 'Order school lunch', 'Pack the requested supplies', 'Confirm transportation with the other caregiver'],
  },
};

function PublicUseCasePage({ content }) {
  const Icon = content.icon;
  return (
    <PublicSiteLayout eyebrow={content.eyebrow} title={content.title} lede={content.lede}>
      <section className="mx-auto grid max-w-7xl gap-8 px-5 py-16 sm:px-8 lg:grid-cols-[0.9fr_1.1fr] lg:py-24">
        <div className="rounded-[28px] bg-slate-950 p-7 text-white shadow-panel sm:p-9">
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-500 text-slate-950"><Icon className="h-7 w-7" aria-hidden="true" /></span>
          <h2 className="mt-7 text-3xl font-black leading-tight">{content.problemTitle}</h2>
          <p className="mt-5 text-base leading-8 text-slate-300">{content.problemCopy}</p>
        </div>
        <div>
          <p className="text-xs font-black uppercase tracking-[0.18em] text-teal-700">How Little Adda helps</p>
          <div className="mt-5 grid gap-4">
            {content.steps.map(([title, copy], index) => (
              <article key={title} className="grid grid-cols-[48px_1fr] gap-4 rounded-[22px] border border-slate-200 bg-white p-5 shadow-subtle">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-50 font-black text-teal-800">0{index + 1}</span>
                <div><h2 className="text-lg font-black">{title}</h2><p className="mt-2 text-sm leading-7 text-slate-600">{copy}</p></div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.18em] text-teal-700">Realistic examples</p>
              <h2 className="mt-4 text-3xl font-black sm:text-4xl">The small details that create family stress.</h2>
              <p className="mt-4 max-w-xl leading-8 text-slate-600">Little Adda is designed around ordinary school communication—not an imaginary perfectly structured inbox.</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {content.examples.map((example) => (
                <div key={example} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 font-bold text-slate-800">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-teal-700" aria-hidden="true" /> {example}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-16 text-center sm:px-8 sm:py-24">
        <div className="rounded-[30px] border border-teal-200 bg-teal-50 p-8 sm:p-12">
          <div className="mx-auto flex w-fit items-center gap-3 text-teal-800"><ShieldCheck className="h-6 w-6" /><UsersRound className="h-6 w-6" /></div>
          <h2 className="mt-5 text-3xl font-black sm:text-4xl">Parents approve the plan.</h2>
          <p className="mx-auto mt-4 max-w-2xl leading-8 text-slate-600">Little Adda suggests next steps. A parent decides what gets added, changed, dismissed, or shared with caregivers.</p>
          <Link to="/signup" className="mt-7 inline-flex items-center gap-2 rounded-2xl bg-teal-700 px-6 py-3 font-black text-white hover:bg-teal-800">Get started <ArrowRight className="h-5 w-5" /></Link>
        </div>
      </section>
    </PublicSiteLayout>
  );
}

export default PublicUseCasePage;
